'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import { type InAppNotificationResponse, useListInAppNotificationsQuery } from '~/data/api/client/workflows'

// Same query args as the Critical overlay, so this subscribes to the cache entry
// the overlay polls (including while the window is unfocused) instead of
// issuing requests of its own.
const CRITICAL_UNREAD_ARGS = { priority: 'Critical', read: false, page: 0, count: 100 } as const
const MAX_INDIVIDUAL_NOTIFICATIONS = 3
const MAX_BODY_LENGTH = 200
const SUMMARY_TAG = 'workflows-critical-summary'

/**
 * Raises native OS notifications for Workflows Critical notifications that
 * appear while the desktop window is unfocused. Mount only inside the desktop
 * shell. It reads the Critical overlay's poll, so it relies on that overlay
 * being mounted.
 *
 * The first poll result is the baseline: rows already unread at launch are
 * never notified. Each later unread id notifies at most once per session, and
 * a row arriving while the window has focus is marked seen silently because
 * the in-app overlay already shows it. More than three new rows in one poll
 * collapse into a single summary notification.
 */
export default function useDesktopCriticalNotifications(): void {
  const t = useTranslations('features.DesktopTitleBar')
  const { data } = useListInAppNotificationsQuery(CRITICAL_UNREAD_ARGS)
  const baselinedRef = useRef(false)
  const seenIdsRef = useRef(new Set<string>())

  useEffect(() => {
    if (!data) return
    const unreadRows = data.data.filter(({ read }) => !read)
    const newRows = unreadRows.filter(({ id }) => !seenIdsRef.current.has(id))
    unreadRows.forEach(({ id }) => seenIdsRef.current.add(id))

    if (!baselinedRef.current) {
      baselinedRef.current = true
      return
    }
    if (newRows.length === 0 || document.hasFocus() || !canNotify()) return

    if (newRows.length > MAX_INDIVIDUAL_NOTIFICATIONS) {
      showNotification(t('criticalSummary', { count: newRows.length }), { tag: SUMMARY_TAG })
      return
    }
    newRows.forEach((row) => showNotification(row.title, { body: truncate(row.body), tag: notificationTag(row) }))
  }, [data, t])
}

function canNotify(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission !== 'denied'
}

function notificationTag({ id }: InAppNotificationResponse): string {
  return `workflows-critical-${id}`
}

function truncate(text: string): string {
  return text.length > MAX_BODY_LENGTH ? `${text.slice(0, MAX_BODY_LENGTH - 1)}…` : text
}

function showNotification(title: string, options: NotificationOptions): void {
  const notification = new Notification(title, options)
  notification.onclick = () => {
    window.focus()
    notification.close()
  }
}
